import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import KeyFeatureCard from "../../components/cards/KeyFeatureCard";
import FaqSection from "../../components/faqsection";
import Footer from "../../components/footer";
import Header from "../../components/header";
import CorporateSection from "../../components/home/CorporateSection";
import CourseSection from "../../components/home/CourseSection";
import FitnessSection from "../../components/home/FitnessSection";
import InstructorSection from "../../components/home/InstructorSection";
import PressSection from "../../components/home/PressSection";
import TemplateSection from "../../components/home/TemplateSection";
import TrustedSection from "../../components/home/TrustedSection";
import Welcome from "../../components/home/Welcome";
import LazyLoadSection from "../../components/loader/LazyLoadSection";
import { useTranslate } from "../../context/TranslationContext";
import useHomeController from "../../controllers/useHomeController";
import { useConnectStripeMutation } from "../../redux/Apis/Auth";
import { useGetFunnelWithFilterQuery } from "../../redux/Apis/Funnel";
import { useGetFeatureQuery } from "../../redux/Apis/HomeFeaturedControl";
import {
  useGetInstructorsQuery,
  useUpdateUserMutation,
} from "../../redux/Apis/User";
import { setMetaTags } from "../../utils/helper";
import "./style.css";

const countries = [
  { code: "gb", label: "United Kingdom" },
  { code: "us", label: "United States" },
  { code: "es", label: "Spain" },
  { code: "de", label: "Germany" },
  { code: "au", label: "Australia" },
];

const options = countries.map((country) => ({
  value: country.code,
  label: (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={`https://flagcdn.com/w40/${country.code}.png`}
        alt={country.label}
        style={{ width: 20, height: 15, marginRight: 10, objectFit: "cover" }}
      />
      <span>{country.label}</span>
    </div>
  ),
}));

const Home = () => {
  const lang = useSelector((state) => state.language.lang);
  const { user } = useSelector((state) => state.authSlice);
  const { t } = useTranslate("keyFeaturesSection");
  const { values, functions } = useHomeController();
  const [selected, setSelected] = useState(options[0]);
  const localCountry = localStorage?.getItem("selectedCountry");
  const userCountry = user?.country;
  const shouldShowBanner = !localCountry && !userCountry;
  const [showBanner, setShowBanner] = useState(shouldShowBanner);

  const userId = values?.user?._id;
  const {
    data: userData,
    isSuccess: userSuccess,
    refetch: refetchUser,
  } = useGetInstructorsQuery(
    { id: userId, params: { lang } },
    { skip: !userId },
  );

  const [connectStripe] = useConnectStripeMutation();

  const stripeAccountId = userData?.data?.user?.creator_obj?.stripe_account_id;
  const stripeStatus = userData?.data?.user?.creator_obj?.status;

  useEffect(() => {
    if (userId) {
      refetchUser();
    }
  }, [userId, refetchUser]);

  const lastTriggeredStatus = useRef(null);

  useEffect(() => {
    if (
      userSuccess &&
      stripeAccountId &&
      stripeStatus === "restricted" &&
      lastTriggeredStatus.current !== "restricted"
    ) {
      lastTriggeredStatus.current = "restricted";

      const connect = async () => {
        try {
          await connectStripe({ params: { lang } }).unwrap();
        } catch (error) {
          console.error("Stripe connection error:", error);
          toast.error(error.message || t("stripeConnectionFailed"));
        }
      };

      connect();
    }
  }, [userSuccess, stripeAccountId, stripeStatus, connectStripe, lang, t]);

  const [updateCountry, { isLoading }] = useUpdateUserMutation();

  const { data: featureData, refetch: refetchFeature } = useGetFeatureQuery({
    params: { lang },
  });

  const showTemplateSection = featureData?.data?.some(
    (item) => item.type === "template" && item.status === true,
  );
  const showCourseSection = featureData?.data?.some(
    (item) => item.type === "course/track" && item.status === true,
  );
  const showCreatorSection = featureData?.data?.some(
    (item) => item.type === "creator" && item.status === true,
  );

  // useEffect(() => {
  //   if (!localStorage?.getItem("selectedCountry")) {
  //     setShowBanner(true);
  //   }
  // }, [values?.isLoggedIn]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  const {
    data: funnelData,
    refetch: refetchFunnel,
    isSuccess: funnelSuccess,
  } = useGetFunnelWithFilterQuery({ tag: "funnel_learner_entry", lang });

  const funnel = funnelSuccess ? (funnelData?.data?.[0] ?? null) : null;

  useEffect(() => {
    refetchFunnel();
    refetchFeature();
  }, [refetchFunnel, refetchFeature]);

  // if (isFeatureLoading || funnelLoading) {
  //   return (
  //     <>
  //       <Header />
  //       <div>
  //         <Loader loading={true} />
  //       </div>
  //     </>
  //   );
  // }

  useEffect(() => {
    const savedCountryCode = localStorage?.getItem("selectedCountry");
    if (!savedCountryCode) return;

    const countryObj = countries.find((c) => c.code === savedCountryCode);
    if (!countryObj) return;

    setSelected({
      value: countryObj.code,
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={`https://flagcdn.com/w40/${countryObj.code}.png`}
            alt={countryObj.label}
            style={{
              width: 20,
              height: 15,
              marginRight: 10,
              objectFit: "cover",
            }}
          />
          <span>{countryObj.label}</span>
        </div>
      ),
    });

    functions.setSelectedCategory(countryObj.code);
  }, [refetchFunnel, refetchFeature, functions]);

  const handleContinue = async () => {
    if (!selected?.value) return;

    localStorage.setItem("selectedCountry", selected.value);

    // IMPORTANT: hide banner immediately
    setShowBanner(false);

    if (!values?.isLoggedIn) {
      window.location.reload();
      return;
    }

    try {
      await updateCountry({
        id: values.user._id,
        payload: { country: selected.value },
        params: { lang },
      }).unwrap();

      functions.setSelectedCategory(localStorage.getItem("selectedCountry"));
    } catch (err) {
      console.error("Failed to update country:", err);
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <Header />
      {/* welcome section starts here */}
      <Welcome
        shouldShowBanner={showBanner}
        user={values.user}
        selected={selected}
        setSelected={setSelected}
        options={options}
        isLoading={isLoading}
        handleContinue={handleContinue}
      />
      {/* welcome section ends here */}

      {/* banner template section starts here */}
      <LazyLoadSection height="400px">
        <TemplateSection showTemplateSection={showTemplateSection} />
      </LazyLoadSection>
      {/* banner template section ends here */}

      {/* top categories section starts here */}
      <LazyLoadSection height="400px">
        <CourseSection showCourseSection={showCourseSection} />
      </LazyLoadSection>
      {/* top categories section ends here */}

      {/* meet instructors section starts here */}
      <LazyLoadSection height="400px">
        <InstructorSection showCreatorSection={showCreatorSection} />
      </LazyLoadSection>
      {/* meet instructors section ends here */}

      {/* latest press section starts here */}
      <LazyLoadSection height="400px">
        <PressSection />
      </LazyLoadSection>
      {/* latest press section ends here */}

      {/* fitness tools section starts here */}
      <LazyLoadSection height="400px">
        <FitnessSection />
      </LazyLoadSection>
      {/* fitness tools section ends here */}

      {/* key features section starts here */}
      <LazyLoadSection height="400px">
        <section className="key-features py-sm-5 py-4">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <h2 className="gallery-title text-uppercase text-white mb-3">
                  {t("title")}
                </h2>
                <p className="mb-4 p-lg l-grey-text">{t("subtitle")}</p>
              </div>
              {t("features", { returnObjects: true }).map((item, index) => (
                <div key={index} className="col-lg-4 col-md-6 my-3">
                  <KeyFeatureCard
                    title={item.title}
                    text={item.text}
                    navigateTo={item.navigateTo}
                    image={item.image}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </LazyLoadSection>
      {/* key features section ends here */}

      {/* trusted by section starts here */}
      <LazyLoadSection height="400px">
        <TrustedSection />
      </LazyLoadSection>
      {/* trusted by section ends here */}

      {/* success stories section starts here */}
      <LazyLoadSection height="400px">
        <CorporateSection />
      </LazyLoadSection>
      {/* success stories section ends here */}

      {/* try our classes section starts here */}
      {funnel && values?.user?.role !== "learner" && (
        <LazyLoadSection height="400px">
          <section className="try-our-classes my-sm-5 my-4">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-7 my-3">
                  <h2 className="text-uppercase">{funnel.title}</h2>
                  <p className="mb-0 p-lg">{funnel.message}</p>
                  <p className="mb-0 mt-2 grey-text ">{funnel?.notes}</p>
                </div>
                <div className="col-lg-5 my-3">
                  <div className="position-relative">
                    <input
                      type="email"
                      placeholder={t("funnelEmailPlaceholder")}
                      className={`site-input pe-5 ${
                        values.error ? "is-invalid" : ""
                      }`}
                      value={values.email}
                      onChange={(e) => {
                        functions.setEmail(e.target.value);
                        if (values.error && values.error.includes("email")) {
                          functions.setError("");
                        }
                      }}
                      id="newsletter-email"
                      name="newsletter-email"
                      autoComplete="email"
                      disabled={values.isNewsletterLoading}
                    />
                    <button
                      className="transparent-btn right-icon"
                      onClick={functions.onSubmitSubscribe}
                      disabled={values.isNewsletterLoading}
                    >
                      <span
                        style={{
                          display: values.isNewsletterLoading
                            ? "inline"
                            : "none",
                        }}
                      >
                        <Spinner
                          size="sm"
                          style={{ color: "var(--primaryColor)" }}
                        />
                      </span>
                      <span
                        style={{
                          display: values.isNewsletterLoading
                            ? "none"
                            : "inline",
                        }}
                      >
                        <i className="fas fa-angle-right" />
                      </span>
                    </button>
                  </div>

                  {values.error && (
                    <div className="invalid-feedback d-block">
                      {values.error}
                    </div>
                  )}

                  <label className="custom-checkbox-label mt-2 mb-0">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={values.agree}
                      onChange={(e) => {
                        functions.setAgree(e.target.checked);
                        if (
                          e.target.checked &&
                          values.error === t("funnelCheckError")
                        ) {
                          functions.setError("");
                        }
                      }}
                      disabled={values.isNewsletterLoading}
                    />
                    <span className="custom-checkbox">
                      <span className="checkmark" />
                    </span>
                    {t("funnelEmailUpdate")}
                  </label>
                </div>
              </div>
            </div>
          </section>
        </LazyLoadSection>
      )}
      {/* try our classes section ends here */}

      {/* faq section starts here */}
      <LazyLoadSection height="400px">
        <FaqSection />
      </LazyLoadSection>
      <Footer />
    </>
  );
};

export default Home;
